import {
  isGroupSetSchema,
  type FieldSchema,
  type FormSchema,
  type GroupSchema,
  type SectionGroupSchema,
} from '../types';
import type { FormService } from './formService';

/**
 * Flatten a form schema into a question id -> field schema lookup.
 *
 * Answers are stored as raw option values ('1', 'grade-b-quarry-manager'), so
 * anything displaying an answer back to the user needs the field schema to
 * recover the option label. Questions only carry their id/label/answer, hence
 * this lookup.
 */
export function collectFieldSchemas(schema: FormSchema): Record<string, FieldSchema> {
  const fieldSchemas: Record<string, FieldSchema> = {};

  const addGroup = (group: GroupSchema) => {
    group.fields?.forEach((fieldSchema) => {
      if (fieldSchema.id) {
        fieldSchemas[fieldSchema.id] = fieldSchema;
      }
    });
  };

  const addGroupOrSet = (groupOrSet: SectionGroupSchema) => {
    if (isGroupSetSchema(groupOrSet)) {
      addGroup(groupOrSet.group);
      return;
    }

    addGroup(groupOrSet);
  };

  schema.steps?.forEach((step) =>
    step.sections?.forEach((section) => section.groups?.forEach(addGroupOrSet))
  );

  return fieldSchemas;
}

/**
 * Dependant fields (DropDownDependant/RadioDependant) keep their options keyed
 * by the answer of the field they depend on. Swap in the option set matching
 * the current parent answer so the schema can be treated like any other.
 *
 * Must be called at render time rather than memoised against the schema — the
 * parent answer changes as the user edits the form.
 */
export function resolveDependantOptions(
  fieldSchema: FieldSchema | undefined,
  formService: FormService
): FieldSchema | undefined {
  if (!fieldSchema?.dependantOptions) {
    return fieldSchema;
  }

  const parent = fieldSchema.dependsOn ? formService.getQuestionById(fieldSchema.dependsOn) : null;
  const parentAnswer = parent?.answer ? String(parent.answer) : '';

  return {
    ...fieldSchema,
    options: fieldSchema.dependantOptions[parentAnswer] ?? fieldSchema.options ?? [],
  };
}
